import React, { FC, useState, useMemo, useCallback, useEffect } from 'react';
import { classNames } from "primereact/utils";
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';


import { stepsKeys, TStep } from '../../types';
import checkPointIcon from '../../../../assets/icons/mailing/check_point_icon.svg';
import { transportTypes } from "../../../../config/config";
import { TSelectedService, TTransportType } from "../../../../@types/global";

import uspsLogo from "../../../../assets/images/usps-logo.png";
import fexdexLogo from "../../../../assets/images/fedex-logo.png";
import freeServiceLogo from "../../../../assets/images/free-service.png";

type TPrintStepItem = {
  heading: string;
  description: string;
};

interface TFinishStepProps {
  isPublic: boolean;
  setStep: React.Dispatch<React.SetStateAction<TStep>>;
  signAndSubmit: (email?: string) => void;
  price?: number;
  uspsPrice?: number;
  isGlobalLoading?:boolean;
  transportType: TTransportType;
  setTransportType: React.Dispatch<React.SetStateAction<TTransportType>>;
  isPaidSelected: boolean;
  selectedService: TSelectedService;
  selectService: React.Dispatch<React.SetStateAction<TSelectedService>>;
  isPublicOpenAi?: boolean;
}

const FinishStep: FC<TFinishStepProps> = ({
  setStep,
  isPublic,
  signAndSubmit,
  price = 0,
  uspsPrice = 0,
  transportType,
  setTransportType,
  isPaidSelected,
  selectedService,
  selectService,
  isPublicOpenAi,
  isGlobalLoading
}) => {

  const [email, setEmail] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputEmail = e.target.value;
    setEmail(inputEmail);
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
    setIsValid(emailRegex.test(inputEmail));
  };

  const showEmailError = !isFocused && email && !isValid;
  const onSelectService = (service: TSelectedService) => {
    if (service === 'paid') {
      setEmail('');
      setIsValid(false);
      setIsFocused(false);
    }
    selectService(service);
  }
  const handleNext = () => {
    setLoading(true);
    if (isPaidSelected) {
      signAndSubmit();
    } else {
      signAndSubmit(email);
    }
  }
  const deliverySteps: TPrintStepItem[] = useMemo(
    () => {
      return (
        [
          {
            heading: 'Send your documents to the IRS',
            description: `We will send your signed cover letter, two copies of your signed 83(b) election form, and a postage-paid return envelope to the IRS.`,
          },
          {
            heading: 'Provide a tracking number and proof of delivery',
            description: `Once your documents are shipped, we will provide you with a tracking number and proof of delivery, if and once available. ${isPublicOpenAi ? '' : 'If you select "USPS Certified Mail", we will also provide you with the postmarked Certified Mail Receipt.'}`,
          },
          {
            heading: 'Provide IRS date-stamped copy',
            description: 'If and once received, we will provide you with the IRS date-stamped copy of your filing.',
          },
          {
            heading: 'Store your documents for 30 days',
            description: "You may request to have any physical documents related to your 83(b) election order shipped to you within 30 days of us obtaining them, for an additional fee."
          },
        ]
      )
    }, [isPublicOpenAi]
  );
  
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'form_submission',
      step:"Select Service",
      form_id: isPublic ? "public-regualr-83b": "regualr-83b",
    });
  },[isPublic]);

  useEffect(() => {
    setLoading(Boolean(isGlobalLoading));
  }, [isGlobalLoading]);  

  const onPaidServiceSelect = useCallback((transport: string) => {
    setTransportType(transport)
    onSelectService('paid')
  }, [onSelectService, setTransportType]);

  return (
    <div className="w-full h-full pt-5 flex justify-content-center relative">
      <div className="p-5 w-9 flex bg-white flex flex-column gap-2 relative">
        <div className="flex flex-grow-1 flex-column relative overflow-hidden">
          <div className="text-2xl font-medium pb-4">
            Please select your preferred shipping method:
          </div>
          <div className="overflow-y-auto flex-grow-1" style={{ maxHeight: isPublic ?'63vh' :"59vh" }}>
            <div className="flex w-full justify-content-between gap-2">
              <div className={classNames("p-3 cursor-pointer border-1 border-200 border-solid flex flex-1 flex-column gap-2", {
                'border-blue-500': transportType === transportTypes.FEDEX && selectedService === 'paid',
                'hover:border-blue-500': transportType !== transportTypes.FEDEX && selectedService === 'paid'
              })} onClick={() => onPaidServiceSelect(transportTypes.FEDEX)}>
                <div className="flex align-items-center gap-2">
                  <div className="w-2 pl-2">
                    <img src={fexdexLogo} alt="FedEx" className="w-full h-auto" />
                  </div>
                  <div className="text-3xl font-medium">FedEx 2Day</div>
                </div>
                <div className="pl-2 font-medium text-lg">
                  <div className="py-2 font-italic">Recommended</div>
                  FedEx 2Day is an IRS-designated private delivery service that is legally
                  equivalent to USPS Certified Mail for purposes of proving timely mailing and delivery.
                  It is typically faster and more reliable.
                </div>
                <div className="pl-2 mt-auto font-bold flex align-items-baseline gap-2">
                  <span className="text-4xl">{`$${price}`}</span>
                  <span className="text-lg font-medium">One-time payment</span>
                </div>
              </div>
              <div className={classNames("p-3 cursor-pointer border-1 border-200 border-solid flex flex-1 flex-column gap-2", {
                'border-blue-500': transportType == transportTypes.USPS && selectedService === 'paid',
                'hover:border-blue-500': transportType !== transportTypes.USPS && selectedService === 'paid'
              })} onClick={() => onPaidServiceSelect(transportTypes.USPS)}>
                <div className="flex align-items-center gap-2">
                  <div className="w-2 pl-2">
                    <img src={uspsLogo} alt="USPS" className="w-full h-auto" />
                  </div>
                  <div className="text-3xl font-medium">USPS Certified Mail</div>
                </div>
                <div className="pl-2 font-medium text-lg">
                  <div className="py-2 font-italic">Conventional</div>
                  USPS Certified Mail is the conventional choice for mailing an 83(b)
                  election to the IRS. This product is priced higher due to additional
                  handling requirements and occasional reliability issues.
                </div>
                <div className="pl-2 mt-auto font-bold flex align-items-baseline gap-2">
                  <span className="text-4xl">{`$${uspsPrice}`}</span>
                  <span className="text-lg font-medium">One-time payment</span>
                </div>
              </div>
              {
                isPublic ? (
                  <div className={classNames("p-3 cursor-pointer border-1 border-200 border-solid flex flex-1 flex-column gap-2", {
                    'border-blue-500': selectedService === 'free',
                    'hover:border-blue-500': selectedService === 'free'
                  })} onClick={() => onSelectService('free')}>
                    <div className="flex align-items-center gap-2">
                      <div className="w-2 sm:w-1 ">
                        <img src={freeServiceLogo} alt="USPS" className="w-full h-auto" />
                      </div>
                      <div className="text-3xl font-medium">I will file it manually myself.</div>
                    </div>
                    <div className="pl-2 font-medium text-lg">
                      We will email you the form you created, and you will send your document to the IRS yourself.
                      <div className="my-2 font-bold underline">
                        We will not take any action for you.
                      </div>
                    </div>
                    <div className="pl-1 mt-auto font-bold flex-column gap-2">
                      <InputText
                        type="email"
                        value={email}
                        className="w-full"
                        onChange={handleEmailChange}
                        placeholder="Your email address"
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)} />
                      <div className="text-red-500" style={{ visibility: showEmailError ? 'visible' : 'hidden' }}>
                        Invalid email address
                      </div>
                    </div>
                  </div>
                ) : null
              }
            </div>
            {
              isPaidSelected ? (
                <div className="flex flex-column gap-2 mt-6">
                  <div className="mb-4">
                    <i className="pi pi-info-circle mr-2" />
                    <span className="font-medium text-2xl">
                      After you sign the cover letter and 83(b) election form, we will:
                    </span>
                  </div>
                  <ul className="list-none flex flex-column gap-2 pl-0">
                    {
                      deliverySteps.map((step, index) => (
                        <li key={index} className="flex align-items-start gap-3 mb-2">
                          <img src={checkPointIcon} alt={step.heading} className="mt-2" />
                          <div className="flex flex-column gap-2">
                            <span className="font-semibold text-xl">{step.heading}</span>
                            <span className="font-medium text-lg">{step.description}</span>
                          </div>
                        </li>
                      ))
                    }
                  </ul>                  
                </div>
              ) : null
            }

          </div>
        </div>
        <div className="-mb-3 mt-auto flex justify-content-end gap-2">
          <Button
            label="Back To Terms"
            severity="secondary"
            onClick={() => setStep(stepsKeys.terms)} />
          <Button
            type="button"
            loading={isLoading}
            onClick={handleNext}
            label={isPaidSelected ? 'Pay' : 'Email'}
            disabled={!isPaidSelected && !isValid}
          />
        </div>
      </div>
    </div>
  );
  // return (
  //   <div className="w-full h-full pt-5 flex flex flex-column justify-content-center align-items-center gap-2 relative">
  //     <div className="p-5 w-8 flex bg-white flex flex-column gap-2 relative" >
  //       <span className="text-2xl font-medium pb-3">
  //         Please select your preferred shipping method:
  //       </span>
  //       <div className="flex w-full justify-content-between gap-2">
  //         <div className={classNames("p-3 cursor-pointer border-1 border-200 border-solid flex flex-1 flex-column gap-2", {
  //           'border-blue-500': transportType === transportTypes.FEDEX && selectedService === 'paid',
  //           'hover:border-blue-500': transportType !== transportTypes.FEDEX && selectedService === 'paid'
  //         })} onClick={() => onPaidServiceSelect(transportTypes.FEDEX)}>
  //           <div className="flex align-items-center gap-2">
  //             <div className="w-2 pl-2">
  //               <img src={fexdexLogo} alt="FedEx" className="w-full h-auto" />
  //             </div>
  //             <div className="text-3xl font-medium">FedEx 2Day</div>
  //           </div>
  //           <div className="pl-2 font-medium text-lg">
  //             <div className="py-2 font-italic">Recommended</div>
  //             FedEx 2Day is an IRS-designated private delivery service that is legally
  //             equivalent to USPS Certified Mail for purposes of proving timely mailing and delivery.
  //             It is typically faster and more reliable.
  //           </div>
  //           <div className="pl-2 mt-auto font-bold flex align-items-baseline gap-2">
  //             <span className="text-4xl">{`$${price}`}</span>
  //             <span className="text-lg font-medium">One-time payment</span>
  //           </div>
  //         </div>
  //         <div className={classNames("p-3 cursor-pointer border-1 border-200 border-solid flex flex-1 flex-column gap-2", {
  //           'border-blue-500': transportType == transportTypes.USPS && selectedService === 'paid',
  //           'hover:border-blue-500': transportType !== transportTypes.USPS && selectedService === 'paid'
  //         })} onClick={() => onPaidServiceSelect(transportTypes.USPS)}>
  //           <div className="flex align-items-center gap-2">
  //             <div className="w-2 pl-2">
  //               <img src={uspsLogo} alt="USPS" className="w-full h-auto" />
  //             </div>
  //             <div className="text-3xl font-medium">USPS Certified Mail</div>
  //           </div>
  //           <div className="pl-2 font-medium text-lg">
  //             <div className="py-2 font-italic">Conventional</div>
  //             USPS Certified Mail is the conventional choice for mailing an 83(b)
  //             election to the IRS. This product is priced higher due to additional
  //             handling requirements and occasional reliability issues.
  //           </div>
  //           <div className="pl-2 mt-auto font-bold flex align-items-baseline gap-2">
  //             <span className="text-4xl">{`$${uspsPrice}`}</span>
  //             <span className="text-lg font-medium">One-time payment</span>
  //           </div>
  //         </div>
  //         {
  //           isPublic ? (
  //             <div className={classNames("p-3 cursor-pointer border-1 border-200 border-solid flex flex-1 flex-column gap-2", {
  //               'border-blue-500': selectedService === 'free',
  //               'hover:border-blue-500': selectedService === 'free'
  //             })} onClick={() => onSelectService('free')}>
  //               <div className="flex align-items-center gap-2">
  //                 <div className="w-2 sm:w-1 ">
  //                   <img src={freeServiceLogo} alt="USPS" className="w-full h-auto" />
  //                 </div>
  //                 <div className="text-3xl font-medium">I will file it manually myself.</div>
  //               </div>
  //               <div className="pl-2 font-medium text-lg">
  //                 We will email you the form you created, and you will send your document to the IRS yourself.
  //                 <div className="my-2 font-bold underline">
  //                   We will not take any action for you.
  //                 </div>
  //               </div>
  //               <div className="pl-1 mt-auto font-bold flex-column gap-2">
  //                 <InputText
  //                   type="email"
  //                   value={email}
  //                   className="w-full"
  //                   onChange={handleEmailChange}
  //                   placeholder="Your email address"
  //                   onFocus={() => setIsFocused(true)}
  //                   onBlur={() => setIsFocused(false)} />
  //                 <div className="text-red-500" style={{ visibility: showEmailError ? 'visible' : 'hidden' }}>
  //                   Invalid email address
  //                 </div>
  //               </div>
  //             </div>
  //           ) : null
  //         }
  //       </div>

  //       <div className="flex flex-column gap-2 mt-6 bg-white -mx-5 px-5" style={{ visibility: isPaidSelected ? 'visible' : 'hidden', maxHeight: '350px', overflowY: 'auto' }}>
  //         <div className="mb-4">
  //           <i className="pi pi-info-circle mr-2" />
  //           <span className="font-medium text-2xl">
  //             After you sign the cover letter and 83(b) election form, we will:
  //           </span>
  //         </div>
  //         <ul className="list-none flex flex-column gap-2 pl-0">
  //           {
  //             deliverySteps.map((step, index) => (
  //               <li key={index} className="flex align-items-start gap-3 mb-2">
  //                 <img src={checkPointIcon} alt={step.heading} className="mt-2" />
  //                 <div className="flex flex-column gap-2">
  //                   <span className="font-semibold text-xl">{step.heading}</span>
  //                   <span className="font-medium text-lg">{step.description}</span>
  //                 </div>
  //               </li>
  //             ))
  //           }
  //         </ul>
  //       </div>
  //     </div>
  //       <div className="p-2 pr-5 -mx-5 mt-auto bottom-0 flex justify-content-end gap-2 bg-white">
  //         <Button
  //           label="Back To Terms"
  //           severity="secondary"
  //           onClick={() => setStep(stepsKeys.terms)} />
  //         <Button
  //           type="button"
  //           loading={isLoading}
  //           onClick={handleNext}
  //           label={isPaidSelected ? 'Pay' : 'Email'}
  //           disabled={!isPaidSelected && !isValid}
  //         />
  //       </div>
  //   </div>
  // )
};

export default FinishStep;
